import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Copy, RefreshCw, Key, Clock, CheckCircle, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvitationCodeCard() {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin"]);

  const [code, setCode] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [remainingMinutes, setRemainingMinutes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Fetch existing code on mount
  useEffect(() => {
    fetchMyCode();
  }, []);

  // Update remaining time every minute
  useEffect(() => {
    if (!expiresAt) return;
    
    const interval = setInterval(() => {
      const now = new Date();
      const expires = new Date(expiresAt);
      const diff = expires - now;
      const minutes = Math.max(0, Math.floor(diff / 60000));
      setRemainingMinutes(minutes);
      
      if (minutes <= 0) {
        setCode(null);
        setExpiresAt(null);
        setRemainingMinutes(null);
      }
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [expiresAt]);

  const fetchMyCode = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/invitation/my-code`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.code) {
          setCode(data.code);
          setExpiresAt(data.expires_at);
          setRemainingMinutes(data.remaining_minutes);
        } else {
          setCode(null);
          setExpiresAt(null);
          setRemainingMinutes(null);
        }
      }
    } catch (error) {
      console.error("Error fetching code:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API}/api/invitation/generate`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setCode(data.code);
        setExpiresAt(data.expires_at);
        setRemainingMinutes(data.valid_for_minutes);
        toast.success(t("admin:toast_davetKoduOlusturuldu"));
      } else {
        const error = await response.json();
        toast.error(error.detail || t("admin:codeCreateFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setGenerating(false);
    }
  };

  const cancelCode = async () => {
    try {
      const response = await fetch(`${API}/api/invitation/cancel`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        setCode(null);
        setExpiresAt(null);
        setRemainingMinutes(null);
        toast.success("Davet kodu iptal edildi");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const copyCode = () => {
    if (code) {
      navigator.clipboard.writeText(code);
      toast.success(t("admin:toast_kodPanoyaKopyalandi"));
    }
  };

  return (
    <Card className="border-2 border-navy/20 bg-gradient-to-br from-navy/5 to-transparent">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Key className="w-5 h-5 text-navy" />
          {t('admin:invitationCode')}
        </CardTitle>
        <CardDescription>
          {t('admin:shareInvitationCodeDesc')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="w-6 h-6 animate-spin text-navy" />
          </div>
        ) : code ? (
          <div className="space-y-4">
            {/* Code Display */}
            <div className="flex items-center gap-2">
              <Input
                value={code}
                readOnly
                className="text-center text-2xl font-mono font-bold tracking-[0.5em] bg-white"
                data-testid="invitation-code-display"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={copyCode}
                className="shrink-0"
                data-testid="copy-code-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Timer */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-amber-600">
                <Clock className="w-4 h-4" />
                <span>
                  {remainingMinutes > 0 
                    ? t("admin:minutesRemaining", { minutes: remainingMinutes })
                    : t("common:aboutToExpire")}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelCode}
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <XCircle className="w-4 h-4 mr-1" />
                {t("admin:cancelCode")}
              </Button>
            </div>
            
            {/* Instructions */}
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
              <p className="font-medium mb-1">{t("admin:giveCodeToBusiness")}</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>{t("admin:businessGoesToAddPage")}</li>
                <li>{t("admin:businessEntersCode")}</li>
                <li>{t("admin:businessAddsYou")}</li>
              </ol>
            </div>
            
            {/* Regenerate Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={generateCode}
              disabled={generating}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`} />
              {t("admin:createNewCode")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 text-center py-2">
              {t('admin:noActiveInvitationCode')}
            </p>
            <Button
              className="w-full bg-navy hover:bg-navy/90"
              onClick={generateCode}
              disabled={generating}
              data-testid="generate-code-btn"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Oluşturuluyor...
                </>
              ) : (
                <>
                  <Key className="w-4 h-4 mr-2" />
                  {t('admin:createInvitationCode')}
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
